import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import { navigationStateFrom } from '@/types/navigation';
import { WorkflowEditor } from '@/components/application/workflow/workflow-editor';
import { useWorkflow, useCreateWorkflow, useUpdateWorkflow, useReanalyzeWorkflow } from '@/hooks/api/use-workflows';
import type { WorkflowNode, WorkflowEdge } from '@/types/workflow';
import { useTranslation } from 'react-i18next';
import { Modal, DialogTrigger, Dialog, ModalOverlay } from '@/components/application/modals/modal';
import { AlertCircle } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';

export const WorkflowEditPage = () => {
    const { workflowId } = useParams<{ workflowId?: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation(['pages:workflows-admin', 'common']);
    const [workflowName, setWorkflowName] = useState('');
    const [showDefaultWarningModal, setShowDefaultWarningModal] = useState(false);
    const hasShownModalRef = useRef<string | null>(null); // Traccia per quale workflowId è già stato mostrato il modal
    // Check if it's a new workflow: either workflowId is 'new' or undefined with pathname '/admin/workflows/new'
    const isNew = workflowId === 'new' || (!workflowId && location.pathname === '/admin/workflows/new');

    // Use API hooks - only call if not new workflow
    const shouldLoadWorkflow = workflowId && workflowId !== 'new';
    const { data: workflow, isLoading } = useWorkflow(shouldLoadWorkflow ? workflowId : '');
    
    const createWorkflowMutation = useCreateWorkflow();
    const updateWorkflowMutation = useUpdateWorkflow();
    const reanalyzeWorkflowMutation = useReanalyzeWorkflow();

    useEffect(() => {
        if (workflow) {
            setWorkflowName(workflow.name);
            // Show warning modal only once when first loading a default workflow
            if (workflow.is_default && workflowId && hasShownModalRef.current !== workflowId) {
                setShowDefaultWarningModal(true);
                hasShownModalRef.current = workflowId;
            }
        } else if (isNew) {
            setWorkflowName('');
            hasShownModalRef.current = null; // Reset quando si crea un nuovo workflow
        }
    }, [workflow, isNew, workflowId]);

    const handleSave = async (nodes: WorkflowNode[], edges: WorkflowEdge[], accountIds?: string[], workflowNameParam?: string, isActive?: boolean, isDefault?: boolean) => {
        const nameToSave = workflowName || workflowNameParam || 'Nuovo Workflow';

        try {
            if (isNew) {
                // Create new workflow - new workflows start in 'draft' status, not active
                const newWorkflow = await createWorkflowMutation.mutateAsync({
                    name: nameToSave,
                    description: '',
                    is_default: isDefault || false,
                    nodes,
                    edges,
                    company_ids: accountIds,
                });
                // Navigation handled by modal in WorkflowEditor
                navigate(`/admin/workflows/${newWorkflow.workflow_id}`, { state: navigationStateFrom(location) });
            } else if (workflowId) {
                // Update existing workflow
                const updateData: any = {
                    name: nameToSave,
                    nodes,
                    edges,
                };

                // Include is_active if provided (user can toggle this)
                if (isActive !== undefined) {
                    updateData.is_active = isActive;
                }

                // Include is_default if provided
                if (isDefault !== undefined) {
                    updateData.is_default = isDefault;
                }

                // Include company_ids only if provided
                if (accountIds !== undefined) {
                    updateData.company_ids = accountIds;
                }

                await updateWorkflowMutation.mutateAsync({
                    workflowId,
                    data: updateData,
                });
                // Navigation handled by modal in WorkflowEditor
            }
        } catch (error) {
            console.error('Error saving workflow:', error);
            // Error already handled by mutation (toast)
        }
    };

    const handleTitleChange = (name: string) => {
        setWorkflowName(name);
    };

    const handleCancel = () => {
        // Always navigate back to workflows list
        navigate('/admin/workflows', { state: navigationStateFrom(location) });
    };

    const handleEvaluate = async () => {
        if (!workflowId || isNew) return;
        await reanalyzeWorkflowMutation.mutateAsync(workflowId);
    };

    if (isLoading && !isNew) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('common:loading')}</p>
                </div>
            </div>
        );
    }

    if (!isNew && !workflow) {
        return (
            <div className="text-center py-12">
                <h3 className="text-lg font-medium text-primary mb-2">{t('workflows.notFound.title')}</h3>
                <p className="text-tertiary mb-4">{t('workflows.notFound.message')}</p>
                <button
                    onClick={() => navigate('/admin/workflows', { state: navigationStateFrom(location) })}
                    className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-secondary shadow-xs ring-1 ring-primary"
                >
                    {t('workflows.notFound.backToList')}
                </button>
            </div>
        );
    }

    return (
        <>
            <div className="h-[calc(100vh-120px)]">
                <WorkflowEditor
                    key={workflow?.workflow_id || 'new'}
                    initialNodes={workflow?.nodes || []}
                    initialEdges={workflow?.edges || []}
                    initialAccountIds={workflow?.company_ids}
                    initialWorkflowName={workflowName}
                    initialStatus={workflow?.status}
                    initialIsActive={workflow?.is_active}
                    initialIsDefault={workflow?.is_default}
                    initialAiFeedback={workflow?.ai_feedback}
                    initialAnalyzedAt={workflow?.analyzed_at}
                    workflowName={workflowName}
                    onTitleChange={handleTitleChange}
                    showBackButton={true}
                    backUrl="/admin/workflows"
                    onSave={handleSave}
                    onEvaluate={!isNew ? handleEvaluate : undefined}
                    onCancel={handleCancel}
                />
            </div>

            {/* Default Workflow Warning Modal */}
            <DialogTrigger isOpen={showDefaultWarningModal} onOpenChange={setShowDefaultWarningModal}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md">
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0">
                                        <AlertCircle className="size-6 text-utility-warning-600" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-primary mb-2">
                                            {t('workflows.modal.defaultWorkflowWarning.title')}
                                        </h3>
                                        <p className="text-sm text-tertiary mb-6">
                                            {t('workflows.modal.defaultWorkflowWarning.message')}
                                        </p>
                                        <div className="flex items-center justify-end gap-3">
                                            <Button
                                                onClick={() => setShowDefaultWarningModal(false)}
                                                size="md"
                                                color="primary"
                                            >
                                                {t('common:modal.close')}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>
        </>
    );
};
